import React, { useState, useRef, useEffect } from "react";
import {
  Upload,
  message,
  Button,
  Modal,
  Select,
  Form,
  Input,
} from "antd";
import btnStyles from "../../../styles/Custom/Button.module.css";
import { getUploadUrlApi } from "../../../services/common.services";
import { convertToFormData } from "../../../helpers/utility";
import {
  // addCategoryApi,
  uploadPatientImagesApi,
} from "../../../services/patient.services";
import useQuery from "../../../hooks/useQuery";
import { CameraOutlined } from "@ant-design/icons";
import axios from "axios";
import { updateIpGalleryFoldersApi } from "../../../services/patient.services";
import modalStyles from "./galleryCompactModal.module.css";
import { getDefaultUploadFileName } from "../../../helpers/galleryUtils";
import UploadFilePreviewList from "./UploadFilePreviewList";

const { Dragger } = Upload;

const PatientGalleryUpload = ({
  refresh,
  isModalVisible,
  setIsModalVisible,
  labels,
  refreshLabelsPage,
  ipId,
  folder,
}) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [uploadFileLoading, setUploadFileLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const params = useQuery();
  const [newLabel, setNewLabel] = useState("");
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const handleRemoveFile = (fileToRemove) => {
    if (fileToRemove?.preview_url?.startsWith("blob:")) {
      URL.revokeObjectURL(fileToRemove.preview_url);
    }

    const filteredFiles = uploadFile.filter(
      (file) => file.path !== fileToRemove.path
    );

    setUploadFile(filteredFiles);

    if (filteredFiles.length === 0) {
      setUploadFileLoading(false);
    }
    message.success("Removed file successfully");
  };

  const handleFileNameChange = (path, value) => {
    setUploadFile((prev) =>
      prev.map((file) =>
        file.path === path ? { ...file, file_name: value } : file
      )
    );
  };

  const uploadProps = {
    beforeUpload: (file, fileList) => {
      const MAX_UPLOAD_SIZE = 30 * 1024 * 1024;

      let file_ext = file.name.split(".").slice(-1)[0].toLowerCase();
      const validImageTypes = ["png", "jpg", "jpeg", "svg"];
      const validVideoTypes = ["mp4", "mov", "avi"];
      const validPdfTypes = ["pdf"];

      const fileType =
        validImageTypes.includes(file_ext) ||
        validVideoTypes.includes(file_ext) ||
        validPdfTypes.includes(file_ext);

      if (!fileType) {
        message.error(`${file.name} is not a valid file type`);
        return Upload.LIST_IGNORE;
      }

      const totalSize = fileList.reduce((sum, f) => sum + f.size, 0);

      if (totalSize > MAX_UPLOAD_SIZE) {
        message.error("The uploading file reaches the maximum limit");
        setUploadFileLoading(true);
        return Upload.LIST_IGNORE;
      }

      setUploadFileLoading(false);
      return true;
    },

    async onChange(info) {
      if (info.file.status === "done") {
        setUploadFileLoading(true);
        try {
          const res = await getUploadUrlApi(
            `path_key=${info.file.name}&admission_type=IP&admission_id=${ipId}`
          );
          const formData = convertToFormData(res, info);
          await axios({
            method: "POST",
            url: res?.data?.url,
            data: formData,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          setUploadFile((prevState) => [
            ...prevState,
            {
              path: res?.data?.fields?.key,
              type: info.file.type,
              file_name: getDefaultUploadFileName(info.file),
              preview_url: URL.createObjectURL(
                info.file.originFileObj || info.file
              ),
            },
          ]);
        } catch (err) {
          message.error(err?.response?.data?.message);
        } finally {
          setUploadFileLoading(false);
        }
      }
    },
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const onFinish = async (val) => {
    const finalFolder = folder || val?.selected_folder;

    if (!finalFolder) {
      message.error("Select a label before upload");
      return;
    }

    const payload = {
      folder: finalFolder,
      paths: uploadFile.map((file) => ({
        path: file.path,
        type: file.type,
        file_name: file.file_name?.trim() || file.path.split("/").pop(),
      })),
      ip_admission: params?.patient,
    };

    if (val?.sub_folder?.trim()) {
      payload.sub_folder = val.sub_folder.trim();
    }

    uploadPatientImagesApi(payload)
      .then((response) => {
        message.success("File uploaded successfully");
        refresh();
        setIsModalVisible(false);
      })
      .catch((error) => {
        message.error(error.response.data.message);
      });
  };

  const handleLabelSelect = (value) => {
    setSelectedCategoryId(value);
  };

  const existingFolders = labels?.map((l) => l.name) || [];
  const onLabelAdd = async () => {
    if (!newLabel?.trim()) {
      message.error("Label name cannot be empty");
      return;
    }

    const updatedFolders = [...new Set([...existingFolders, newLabel.trim()])];

    try {
      await updateIpGalleryFoldersApi(ipId, {
        gallery_folders: updatedFolders,
      });

      message.success("Label added successfully");

      refreshLabelsPage();
      setNewLabel("");
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update label");
    }
  };

  return (
    <>
      <Modal
        width={isMobile ? "min(450px, calc(100vw - 24px))" : 450}
        {...(isMobile ? { centered: false } : {})}
        wrapClassName={
          isMobile
            ? `${modalStyles.compactModalWrap} ${modalStyles.galleryUploadModalWrap}`
            : undefined
        }
        styles={{
          content: isMobile
            ? {
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100dvh - 32px)",
                overflow: "hidden",
              }
            : undefined,
          body: isMobile
            ? {
                flex: "1 1 auto",
                minHeight: 0,
                maxWidth: "100%",
                overflowX: "hidden",
                overflowY: "auto",
                boxSizing: "border-box",
              }
            : undefined,
          footer: isMobile
            ? {
                flexShrink: 0,
                marginTop: 0,
                background: "#fff",
                borderTop: "1px solid #f0f0f0",
              }
            : undefined,
        }}
        title={folder ? `Upload to ${folder}` : "Upload"}
        onCancel={() => setIsModalVisible(false)}
        open={isModalVisible}
        footer={
          <div
            className="flex-end"
            style={{
              gap: isMobile ? 12 : 24,
              marginTop: isMobile ? 0 : 20,
            }}
          >
            <Button
              type="primary"
              ghost
              style={{ fontWeight: "500" }}
              className={btnStyles.mdBtn}
              onClick={() => setIsModalVisible(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={uploadFileLoading}
              type="primary"
              style={{ fontWeight: "500" }}
              className={btnStyles.mdBtn}
              onClick={() => {
                if (formRef.current) {
                  formRef.current.submit();
                }
              }}
            >
              Save
            </Button>
          </div>
        }
      >
        <Form
          layout="vertical"
          form={form}
          ref={formRef}
          onFinish={onFinish}
          className={isMobile ? modalStyles.compactForm : undefined}
        >
          {!folder && (
            <Form.Item
              label="Select Label"
              name="selected_folder"
              rules={[{ required: true, message: "Please select a label" }]}
            >
              <Select placeholder="Select label to upload">
                {labels?.map((item) => (
                  <Select.Option value={item.name} key={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item label="Sub Folder" name="sub_folder">
            <Input placeholder="Enter Sub Folder" />
          </Form.Item>

          <Form.Item
            label="Upload Gallery"
            name="upload"
            rules={[
              {
                required: uploadFile?.length === 0 ? true : false,
                message: "Please upload files",
              },
            ]}
          >
            <Dragger
              className={isMobile ? modalStyles.compactDragger : undefined}
              style={
                isMobile
                  ? {
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                    }
                  : { width: "400px" }
              }
              accept=".png,.jpg,.jpeg,.svg,.mp4,.mov,.avi,.pdf"
              {...uploadProps}
              multiple
              customRequest={dummyRequest}
              showUploadList={false}
            >
              <p className="ant-upload-drag-icon">
                <CameraOutlined />
              </p>
              <p className="ant-upload-text">
                <span style={{ color: "#406AFF" }}>Click to Capture </span> or
                upload
              </p>
              {/* <p className="ant-upload-hint">
                PDF, SVG, JPEG, JPG, PNG, MP4, MOV, AVI
              </p> */}
            </Dragger>
            {isMobile ? (
              <div className={modalStyles.uploadFileListScroll}>
                <UploadFilePreviewList
                  uploadFile={uploadFile}
                  onRemove={handleRemoveFile}
                  onFileNameChange={handleFileNameChange}
                  isMobile={isMobile}
                />
              </div>
            ) : (
              <UploadFilePreviewList
                uploadFile={uploadFile}
                onRemove={handleRemoveFile}
                onFileNameChange={handleFileNameChange}
                isMobile={isMobile}
              />
            )}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PatientGalleryUpload;
